import { editorClient } from "@/lib/editorClient";
import { organizationProjectsQueryKey } from "@/queries/organization";
import { Trans } from "@lingui/react/macro";
import { CardGrid, ContentCard, IconButton } from "@op/sf-editor";
import { FeatherSettings } from "@subframe/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import z from "zod";

const paramsSchema = z.object({
  organizationSlug: z.string(),
});

async function getOrganizationProjects(organizationSlug: string) {
  const res = await editorClient.organizations[
    ":organizationSlug"
  ].projects.$get({
    param: {
      organizationSlug,
    },
  });
  if (!res.ok) {
    if (res.status === 404) throw notFound();
    else throw new Error("Failed to fetch organization projects");
  }
  const data = await res.json();
  return data;
}

export const Route = createFileRoute("/app/$organizationSlug/")({
  params: paramsSchema,
  loader: async ({ params: { organizationSlug } }) => {
    const organizationProjects =
      await getOrganizationProjects(organizationSlug);
    return { organizationProjects };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { organizationSlug } = Route.useParams();
  const { organizationProjects } = Route.useLoaderData();
  const queryClient = useQueryClient();
  const { data } = useQuery(
    {
      queryKey: organizationProjectsQueryKey(organizationSlug),
      initialData: organizationProjects,
      queryFn: () => getOrganizationProjects(organizationSlug),
    },
    queryClient,
  );

  return (
    <div className="flex h-full w-full max-w-[1280px] flex-col items-start gap-8 px-4 mx-auto mt-12">
      <div className="flex w-full flex-col items-start gap-4">
        <div className="flex flex-col items-start">
          <span className="text-caption font-caption text-subtext-color">
            {data?.organization.name}
          </span>
          <span className="text-heading-1 font-heading-1 text-default-font">
            <Trans>Projects</Trans>
          </span>
        </div>
        <CardGrid>
          {data?.projects.map((project) => (
            <ContentCard
              key={project.id}
              settingsSlot={
                <Link
                  to="/app/$organizationSlug/$projectSlug/settings"
                  params={{
                    organizationSlug,
                    projectSlug: project.slug,
                  }}
                  className="contents"
                >
                  <IconButton
                    variant="brand-tertiary"
                    size="medium"
                    icon={<FeatherSettings />}
                  />
                </Link>
              }
            >
              <Link
                to="/app/$organizationSlug/$projectSlug"
                params={{ organizationSlug, projectSlug: project.slug }}
                className="contents"
              >
                <ContentCard.Label>
                  <Trans>Project</Trans>
                </ContentCard.Label>
                <ContentCard.Title>{project.name}</ContentCard.Title>
              </Link>
            </ContentCard>
          ))}
        </CardGrid>
      </div>
    </div>
  );
}
